import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionCard } from './InteractionCard';
import type { HubContext, HubInteraction } from '../../types/hub';
import { Rocket } from 'lucide-react';
import { formatTimeAgo } from '../../utils/formatTimeAgo';

interface InteractionFeedProps {
  interactions: HubInteraction[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  currentUserId: string;
  context: HubContext;
}

export const InteractionFeed: React.FC<InteractionFeedProps> = ({
  interactions,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  currentUserId,
  context,
}) => {
  const { t } = useTranslation(['hub', 'quotes', 'common', 'notifications']);

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    const container = scrollRef.current;
    if (!trigger || !container || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root: container, rootMargin: '50px', threshold: 0 }
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, interactions.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">{t('loading')}</p>
      </div>
    );
  }

  if (interactions.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-300 py-12 text-center">
        <p className="text-gray-500">{t('noInteractions')}</p>
      </div>
    );
  }

  const acceptedQuote = context.job.quotes.find(quote => quote.status === 'accepted');

  return (
    <div ref={scrollRef} className="flex max-h-[500px] flex-col overflow-y-auto">
      <div ref={loadMoreTriggerRef} className="h-1" />
      {loadingMore && <div className="py-2 text-center text-sm text-gray-500">{t('loadMore')}</div>}
      <div className="flex flex-col gap-2">
        {acceptedQuote && (
          <div className="w-full rounded-full bg-emerald-100 border border-emerald-200 py-1 flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-600 text-white">
              <Rocket size={20} />
            </div>

            <div className="flex flex-col">
              <p className="text-emerald-800 font-semibold">
                {t('submission_accepted_hint', { name: acceptedQuote.quote_owner.first_name })}
              </p>
              {
                <p className="text-emerald-700 text-sm">
                  {formatTimeAgo(acceptedQuote.created_at)}
                </p>
              }
            </div>
          </div>
        )}
        {interactions.map(interaction => (
          <div key={interaction.id}>
            <InteractionCard
              context={context}
              interaction={interaction}
              currentUserId={currentUserId}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
